import { Controller } from '@hotwired/stimulus';
import { useDebounce } from 'stimulus-use';
import { pausePlayers } from '../../utils/pausePlayers';
/*
 * Description
 * -------
 *
 * Manages mux-player.
 *
 * Must be used on mux-player element.
 * Can be used multiple times in the document.
 *
 * Targets
 * -------
 *
 * nextVideoSuggestion - (optional) - next video suggestion element to show when video is finished
 *
 * Values
 * -------
 *
 * time                - (required) - saved time in seconds
 * saveTime            - (required) - wether save time during playback
 * timesPath           - (required) - API endpoint to create and update time
 * finishTimesPath     - (required) - API endpoint to update times on finish
 * title               - (required) - video title
 * creatorName         - (required) - creator name
 * mediaSessionArtwork - (required) - media session artwork url
 *
 * Usage
 * -------
 *
 * <mux-player
 *   data-controller="mux-player"
 *   data-mux-player-time-value="0"
 *   data-mux-player-save-time-value="true"
 *   data-mux-player-times-path-value="https://example.com"
 *   data-mux-player-finish-times-path-value="https://example.com"
 *   data-mux-player-title-value="Video title"
 *   data-mux-player-creator-name-value="Creator name"
 *   data-mux-player-media-session-artwork-value="https://example.com"
 * >
 * </mux-player>
 */
export class MuxPlayer extends Controller {
    static targets = ['nextVideoSuggestion'];
    static values = {
        time: Number,
        saveTime: Boolean,
        timesPath: String,
        finishTimesPath: String,
        title: String,
        creatorName: String,
        mediaSessionArtwork: String,
    };
    static debounces = ['updateTimeOnSeek', 'createTime'];
    async connect() {
        // Must be imported before any other mux-player import
        await import('./themes/media-theme-fourthwall-audio');
        await import('./themes/media-theme-fourthwall-audio-message');
        await import('@mux/mux-player');
        useDebounce(this, {
            wait: 2000,
        });
        this.element.addEventListener('play', () => {
            this.createMediaSession();
            pausePlayers(this.element);
        });
        this.element.addEventListener('canplay', () => {
            this.createTime();
        }, { once: true });
        this.element.addEventListener('seeked', () => {
            this.updateTimeOnSeek();
        });
        this.element.addEventListener('ended', () => {
            this.updateTimeOnFinish();
            this.showNextVideoSuggestion();
        });
    }
    createMediaSession() {
        if ('mediaSession' in navigator && window.MediaMetadata) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: this.titleValue,
                artist: this.creatorNameValue,
                artwork: [
                    {
                        src: this.mediaSessionArtworkValue,
                        sizes: '128x128',
                        type: 'image/png',
                    },
                ],
            });
        }
    }
    async createTime() {
        if (!this.saveTimeValue)
            return;
        if (this.timeValue === -1) {
            const response = await fetch(this.timesPathValue, { method: 'POST' });
            if (!response.ok)
                return;
            this.scheduleUpdateTime();
        }
        else {
            this.scheduleUpdateTime();
        }
    }
    scheduleUpdateTime() {
        this.updateTimeInterval = setInterval(() => {
            if (this.element.paused)
                return;
            this.updateTime();
        }, 2000);
    }
    async updateTime() {
        const seconds = Math.floor(this.element.currentTime);
        if (seconds <= 0)
            return;
        await fetch(this.timesPathValue, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ seconds: seconds }),
        });
    }
    updateTimeOnSeek() {
        if (!this.updateTimeInterval)
            return;
        if (!this.element.paused)
            return;
        this.updateTime();
    }
    async updateTimeOnFinish() {
        const seconds = Math.floor(this.element.currentTime);
        if (!this.hasFinishTimesPathValue)
            return;
        if (!this.saveTimeValue)
            return;
        if (seconds <= 0)
            return;
        if (seconds > 0) {
            await fetch(this.finishTimesPathValue, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ seconds: seconds }),
            });
        }
    }
    showNextVideoSuggestion() {
        if (!this.hasNextVideoSuggestionTarget)
            return;
        this.nextVideoSuggestionTarget.classList.remove('hidden');
    }
    hideNextVideoSuggestion() {
        if (!this.hasNextVideoSuggestionTarget)
            return;
        this.nextVideoSuggestionTarget.classList.add('hidden');
    }
    disconnect() {
        if (!this.updateTimeInterval)
            return;
        clearInterval(this.updateTimeInterval);
    }
}
